import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, DialogType, InlineAlertType, InlineAlertWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import ReactMarkdown = require('react-markdown');
import _ = require('underscore');

import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { ModalInstance, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { imgPath } from '~commonInfra/utils/ImgUrl';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Formatters, Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { DeleteCredentialRequest } from '~src/connectivity/serverApi/requests/credentials/DeleteCredentialRequest';
import { getMsTeamsIntegrationsForSite } from '~src/connectivity/serverApi/requests/credentials/GetIntegrationsForSiteRequest';
import { IListCredentialModelsResult, ListCredentialModelsRequest } from '~src/connectivity/serverApi/requests/credentials/ListCredentialModelsRequest';
import { removeIntegrationFromSite } from '~src/connectivity/serverApi/requests/credentials/RemoveIntegrationFromSiteRequest';
import { AddOAuthClientParams } from '~src/connectivity/serverApi/requests/OAuth/AddOAuthClientsRequest';
import { IGetOAuthClientsResult } from '~src/connectivity/serverApi/requests/OAuth/GetOAuthClientsRequest';
import { ICredentialModel } from '~src/connectivity/serverApi/types/credentials/ICredentialModel';
import { IGetIntegrationsForSiteResponse, IntegrationContext } from '~src/connectivity/serverApi/types/credentials/IGetIntegrationsForSiteResponse';
import { IOAuthClient } from '~src/connectivity/serverApi/types/OAuth/IOAuthClient';
import { OAuthCredentialTypes } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { AddOAuthCredentialAction } from '~src/connectivity/services/AddOAuthCredentialAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { AddOAuthClientDialog, AddOAuthClientDialogProps, OAuthClientSetting } from '~src/settings/general-settings/components/sections/oauth-clients-registry/AddOAuthClientDialog';
import { addOAuthClients, deleteOAuthClients, fetchOAuthClients, renderOAuthClientListTableHeader, renderOAuthClientTableRow } from '~src/settings/general-settings/components/sections/oauth-clients-registry/OAuthClientsRegistry';
import { addOAuthClientDialogStyle } from '~src/settings/general-settings/styles/oauth-client/OAuthClientsRegistryStyles';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';

import { integrationSettingConnectButton, msTeamsAddOauthClientButton, msTeamsConnectivityMainHeader, msTeamsConnectText, msTeamsDisconnectText, msTeamsInfoText, msTeamsLegalConsentMessage, msTeamsLogo, msTeamsSettingsHeaderContainer, msTeamsUpdateAvailableText, msTeamsUpdateAvailableTitle } from '../styles/IntegrationsSettingsStyles';
import { sendConnectToMsTeamsTelemetry, sendUpgradeMsTeamsConnectionTelemetry } from './MsTeamsIntegrationSettingsTelemetry';

export const ConnectToMsTeams = 'connect-to-msteams';
export const LegacyVirtualRecordId = 'v1|virtual|context';

export interface MsTeamsIntegrationSettingsState {
  connected: boolean | undefined;
  oAuthClients: OAuthClientSetting[];
  integrationInfo: ICredentialModel | IntegrationContext;
  getMsTeamsIntegrationInfoError: boolean;
  getOauthClientError: boolean;
  termsAccepted: boolean;
}
export interface MsTeamsIntegrationSettingsStateProps {
  siteLuid: string;
}

export type MsTeamsIntegrationSettingsProps =  MsTeamsIntegrationSettingsStateProps;

export class MsTeamsIntegrationSettingsComponent extends React.Component<MsTeamsIntegrationSettingsProps, MsTeamsIntegrationSettingsState> {
  constructor(props: MsTeamsIntegrationSettingsProps) {
    super(props);
    this.state = { connected: undefined, integrationInfo: undefined, oAuthClients: [], getMsTeamsIntegrationInfoError: false, getOauthClientError: false, termsAccepted: false };
  }

  componentDidMount() {
    this.updateOAuthClients();
    this.getMsTeamsIntegrationInfo();
  }

  /**
   * Retrieve teams integration summary (if exists) for the given site,
   * and set the 'connected' state field accordingly
   */
  getMsTeamsIntegrationInfo() {
    const sipEnabled =   FeatureFlags.msTeamsIntegrationPlatformEnabled;
    if (this.isTableauOnPrem() || !sipEnabled) {
      let getIntegrationInfoResult = getMsTeamsIntegrationSummaryOnPrem();
      getIntegrationInfoResult.then((result: IListCredentialModelsResult) => {
        this.processIntegrationInfoResult(result.errors?.length > 0, result.credentialModels);
      });
    } else {
      let getIntegrationInfoResult = getMsTeamsIntegrationsForSite({ siteLuid: this.props.siteLuid, type: 'MSTEAMS' });
      getIntegrationInfoResult.then((result: IGetIntegrationsForSiteResponse) => {
        this.processIntegrationInfoResult(false, result.integrations);
      }).catch(() => this.processIntegrationInfoResult(true));
    }
  }

  processIntegrationInfoResult(errors?: boolean, integrationInfoModels?: ICredentialModel[] | IntegrationContext[]) {
    if (errors) {
      this.setState({ connected: false, getMsTeamsIntegrationInfoError: true });
    } else if (integrationInfoModels && integrationInfoModels.length > 0) {
      this.setState({ connected: true, integrationInfo: integrationInfoModels[0] });
    } else {
      this.setState({ connected: false });
    }
  }

  /**
   * Retrieve the MS Teams OAuth client (if exists) which has been added to this site
   */
  private updateOAuthClients() {
    let oAuthClients: IOAuthClient[];
    let clientSettings: OAuthClientSetting[];
    let errorFetchingClients: boolean;
    fetchOAuthClients().then((result: IGetOAuthClientsResult) => {
      if (result.errors) {
        errorFetchingClients = true;
      } else {
        errorFetchingClients = false;
        oAuthClients = result.oauthClients.filter(client => client.type === OAuthCredentialTypes.MsTeams);
        clientSettings = oAuthClients.map((v: IOAuthClient) => {
          const clientSetting: OAuthClientSetting = {
            dbClass: v.type,
            instanceUrl: '',
            oauthClientId: v.clientId,
            oauthClientSecret: '',
            oauthClientRedirectUri: '',
            id: v.id
          };
          return clientSetting;
        });
      }
    }).then(() => {
      this.setState({ oAuthClients: clientSettings, getOauthClientError: errorFetchingClients });
    });
  }

  render() {
    //TODO: Replace with Teams_Integration_Logo.svg
    const imageUrl = imgPath('ms_teams_logo_temp.png');

    return (<div>
      <div className={msTeamsSettingsHeaderContainer}>
          <img
            data-tb-test-id={`${ConnectToMsTeams}-logo`}
            className={msTeamsLogo}
            src={imageUrl}
            alt={Messages.connectToMsTeams_msTeamsLogo()}
          />
          <h2 className={msTeamsConnectivityMainHeader}>{Messages.connectToMsTeams_settingTitle()}</h2>
        </div>
      {this.renderMsTeamsAppInfoSection()}
      {this.isTableauOnPrem() && this.renderOAuthSection()}
      {this.renderConnectToMsTeamsSection()}
    </div>
    );
  }

  /**
   * General Tableau on MS Teams info section and learn more link
   */
  renderMsTeamsAppInfoSection() {
    const isUpgradeAvailable = this.state.connected && this.getUpgradeRequired();
    const upgradeButton =
      <ButtonWidget testId={`${ConnectToMsTeams}-upgrade-button`} disabled={!this.state.termsAccepted} buttonType={ButtonType.Outline} onClick={() => this.handleUpgrade()}>
        {Messages.connectToMsTeams_upgradeButton()}
      </ButtonWidget>;

    return (
      <div data-tb-test-id={`${ConnectToMsTeams}-info-section`}>
        <h3 data-tb-test-id={`${ConnectToMsTeams}-description-header`} className={SettingsStyles.indent}>{Messages.connectToMsTeams_descriptionHeader()}</h3>
        <div className={SettingsStyles.indent}>
          {isUpgradeAvailable && (
            <InlineAlertWidget
              type={InlineAlertType.Info}
              testId={`${ConnectToMsTeams}-upgrade-alert`}
              button={upgradeButton}
              style={{ display: 'inline-flex', marginTop: '8px' }} >
                <div className={msTeamsUpdateAvailableTitle}>{Messages.connectToMsTeams_upgradeAvailableAlert()}</div>
                <div className={msTeamsUpdateAvailableText}>{Messages.connectToMsTeams_upgradeAvailableMsg()}</div>
                {this.getLegalInfo('upgrade')}
            </InlineAlertWidget>
          )}
          <div data-tb-test-id={`${ConnectToMsTeams}-setting-description`} className={msTeamsInfoText}>
            {Messages.connectToMsTeams_settingDescription()}
          </div>
          <ReactMarkdown
            data-tb-test-id={`${ConnectToMsTeams}-msteams-privacy-info`}
            source={Messages.connectToMsTeams_privacyPolicy()}
          />
          <div className={msTeamsInfoText} data-tb-test-id={`${ConnectToMsTeams}-required-section`}>
            <span><strong>{Messages.connectToMsTeams_required()}</strong></span>
            <span data-tb-test-id={`${ConnectToMsTeams}-required-section-description`}>
              {this.isTableauOnPrem() ? Messages.connectToMsTeams_requirementDescription_onprem() : Messages.connectToMsTeams_requirementDescription_online()}
            </span>
            <TextLinkWidget
              testId={`${ConnectToMsTeams}-info-section-learn-more-link`}
              kind={TextLinkKind.Standard}
              className={learnMoreStyle}
              //TODO: Create help link for Integrating MS Teams with Tableau
              href={HelpService.getHelpUrlByCategory('server-slack-config')}
              target={'_blank'}
              rel={'noreferrer noopener'}>
              {Messages.connectToMsTeams_LearnMore()}
            </TextLinkWidget>
          </div>
        </div>
      </div>
    );
  }

  handleUpgrade() {
    const sessionManagementService = getSessionManagementServiceInstance();
    AddOAuthCredentialAction.attemptAuth(OAuthCredentialTypes.MsTeams, null, null, null, null, /* requiresUpgrade: */ true).then((credentialId) => {
      if (credentialId) {
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToMsTeams_toastUpgradeSuccessMsg() });
        sendUpgradeMsTeamsConnectionTelemetry({
          userId: sessionManagementService.sessionUserId(),
          siteLuid: sessionManagementService.sessionSiteLuid(),
          version: (this.state.integrationInfo as IntegrationContext).external?.msTeams?.appVersion,
        });
      }
      this.getMsTeamsIntegrationInfo();
    });
  }

  /**
   * Includes "Add OAuth Client" logic, user instructions, and table
   */
  renderOAuthSection() {
    return (
      <div data-tb-test-id={`${ConnectToMsTeams}-oauth-section`} className={SettingsStyles.indent}>
        <h4 data-tb-test-id={`${ConnectToMsTeams}-oauth-header`}>{Messages.connectToMsTeams_msTeamsOAuthClientRegistry()}</h4>
        <span data-tb-test-id={`${ConnectToMsTeams}-oauth-description`} className={msTeamsInfoText}>{Messages.connectToMsTeams_registerMsTeamsOAuthClient()}</span>
        <TextLinkWidget
          testId={`${ConnectToMsTeams}-learn-more-link`}
          className={learnMoreStyle}
          kind={TextLinkKind.Standard}
          href={HelpService.getHelpUrlByCategory('server-oauth-registry')}
          target={'_blank'}
          rel={'noreferrer noopener'}>
          {Messages.connectToMsTeams_LearnMore()}
        </TextLinkWidget>
        {this.getAddOAuthClientButton()}
        {this.renderOAuthClientTable()}
      </div>
    );
  }

  getLegalInfo(useCase: String) {
    return(
      <div data-tb-test-id={`${ConnectToMsTeams}-legal-section`}>
        <ReactMarkdown className={msTeamsLegalConsentMessage} renderers={{paragraph: 'div'}} source={Messages.connectToMsTeams_salesforceTermsOfServiceContent()} />
        <CheckBoxWithLabelWidget
          checked={this.state.termsAccepted}
          handleChange={() => this.setTermsAccepted(!this.state.termsAccepted)}
          testId={`${ConnectToMsTeams}-${useCase}-legal-checkbox`}
          label={Messages.connectToMsTeams_salesforceTermsOfServiceAccept()}
          containerStyle={{marginLeft: 0}} />
      </div>
    );
  }

  getAddOAuthClientButton() {
    return <ButtonWidget
      buttonType={ButtonType.Outline}
      testId='add-oauth-client-on-site-button'
      className={msTeamsAddOauthClientButton}
      onClick={() => this.openOAuthModal()}
      disabled={this.state && this.state.oAuthClients && this.state.oAuthClients.length > 0}
      type='button'>{Messages.addOAuthClient_action()}
    </ButtonWidget>;
  }

  handleAddOAuthClient(oauthClientSetting: OAuthClientSetting) {
    const oauthParams: AddOAuthClientParams = {
      dbClass: oauthClientSetting.dbClass,
      instanceUrl: oauthClientSetting.instanceUrl,
      clientId: oauthClientSetting.oauthClientId,
      clientSecret: oauthClientSetting.oauthClientSecret,
      redirectUri: oauthClientSetting.oauthClientRedirectUri
    };
    addOAuthClients([oauthParams]).then((result: IResult) => {
      if (result.errors) {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.connectToMsTeams_addOauthClient_error() });
      } else {
        this.updateOAuthClients();
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToMsTeams_addOauthClient_success() });
      }
    });
  }

  openOAuthModal() {
    let modalInstance: ModalInstance;
    const dialogProps: AddOAuthClientDialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      addOAuthClientSubmit: (formikValues: OAuthClientSetting) => this.handleAddOAuthClient(formikValues),
      hardcodeConnectionType: OAuthCredentialTypes.MsTeams,
      showInstanceUrlSection: false
    };

    modalInstance = ReactModalService.open({
      type: DialogType.Uber,
      dialogContent: React.createElement(AddOAuthClientDialog, dialogProps),
      testId: 'addOAuthClientDialog',
      showCloseButton: true,
      dialogStyle: addOAuthClientDialogStyle,
      shouldNotSetDefaultWidth: true
    });

    modalInstance.result.catch((reason) => {
      if (reason === 'dismissDialog' || reason === 'cancel') {
        Promise.resolve();
      } else Promise.reject();
    });
  }

  renderOAuthClientTable() {
    const deleteAction = (id: string) => deleteOAuthClients([id]);
    const deleteClient = (client: OAuthClientSetting) => {
      let description = Messages.connectToMsTeams_deleteOauthClient_description();
      let actionName = Messages.connectToMsTeams_deleteOauthClient_actionName();
      let header = Messages.connectToMsTeams_deleteOauthClient_header();
      this.openDeleteConfirmationDialog(description, header, actionName, deleteAction, client.id).then((result) => {
        if (result.errors) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.connectToMsTeams_deleteOauthClient_error() });
        } else {
          this.updateOAuthClients();
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToMsTeams_deleteOauthClient_success() });
        }
      });
    };
    let stateLoaded = this.state && this.state.oAuthClients;
    if (!stateLoaded) return;
    let displayMessage = this.state.getOauthClientError ? Messages.connectToMsTeams_oAuthClients_errorState() : Messages.connectToMsTeams_oauthTable_emptyState();
    let client = this.state.oAuthClients[0];
    let emptyState = _.isUndefined(client) || _.isNull(client);
    return (
      <table id='oauth-clients-list-table-id' className='tb-static-grid-table tb-static-grid-table-settings-width'>
        <thead>
          <tr>
            {renderOAuthClientListTableHeader(true)}
          </tr>
        </thead>
        <tbody id='oauth-clients-list-body-id'>
          {renderOAuthClientTableRow({ client, deleteClient, isMsTeamsOAuthClient: true }, emptyState, this.state.connected, displayMessage)}
        </tbody>
      </table>
    );
  }

  /**
   * Includes the button to connect to msTeams, instructions, and msTeams credentials table
   */
  renderConnectToMsTeamsSection() {
    let isConnectToMsTeamsButtonDisabled = false;
    let legalTextRequired = false;
    if (!this.isTableauOnPrem() && this.state.connected === false && FeatureFlags.msTeamsIntegrationPlatformEnabled) {
      legalTextRequired = true;
      if (!this.state.termsAccepted) {
        isConnectToMsTeamsButtonDisabled = true;
      }
    }
    if (this.isTableauOnPrem()) {
      isConnectToMsTeamsButtonDisabled = (this.state.oAuthClients.length === 0 && !this.state.connected);
    }

    return (
      <div data-tb-test-id={`${ConnectToMsTeams}-section`} className={SettingsStyles.indent}>
        {this.renderConnectToMsTeamsHeaderAndDescription()}
        <div>
        {legalTextRequired && (
            <InlineAlertWidget
              type={InlineAlertType.Info}
              testId={`${ConnectToMsTeams}-connect-legal-notice`}
              style={{ display: 'inline-flex', marginBottom: '16px' }} >
                {this.getLegalInfo('connect')}
            </InlineAlertWidget>)}
          <ButtonWidget
            testId={`${ConnectToMsTeams}-button`}
            className={integrationSettingConnectButton}
            handleClick={() => { this.handleConnectToMsTeams(this.state.connected, this.state.integrationInfo && this.getIntegrationInfoLuid()); }}
            buttonType={ButtonType.Outline}
            disabled={isConnectToMsTeamsButtonDisabled || this.state.getMsTeamsIntegrationInfoError}
            showSpinner={this.state.connected === undefined}
          >
            {(this.state.connected) ? Messages.connectToMsTeams_disconnect() : Messages.connectToMsTeams()}
          </ButtonWidget>
        </div>
        {this.state.connected && (
          <div data-tb-test-id={`${ConnectToMsTeams}-disconnect-description`} className={msTeamsDisconnectText}>
            {Messages.connectToMsTeams_disconnectFromMsTeams_description()}
          </div>
          )}
        {this.renderMsTeamsConnectionTable()}
      </div>
    );
  }

  renderConnectToMsTeamsHeaderAndDescription() {
    if (!this.isTableauOnPrem()) {
      // In TOL we do not display a title + description above the MS Teams button.
      // Add a space above the "Connect to Microsoft Teams" button
      return <div data-tb-test-id={`${ConnectToMsTeams}-line-height-space`} style={{ height: CommonStyles.Sizes.LineHeight }}></div>;
    }

    return (
      <div>
        <h4 data-tb-test-id={`${ConnectToMsTeams}-header`}>{Messages.connectToMsTeams()}</h4>
        <div data-tb-test-id={`${ConnectToMsTeams}-connect-instructions`} className={msTeamsConnectText}>{Messages.connectToMsTeams_connectToMsTeamsWorkspaceDescription()}</div>
      </div>
    );
  }

  renderMsTeamsConnectionTable() {
    let workspaceName = this.state.connected ? this.getWorkspaceName() :
      this.state.getMsTeamsIntegrationInfoError ? Messages.connectToMsTeams_msTeamsCredentials_errorState() : Messages.connectToMsTeams_notConnectedToWorkspace();
    let connectedOn = '';
    if (this.state.connected) {
      let dateString = isFromRest(this.state.integrationInfo) ? (this.state.integrationInfo as unknown as IntegrationContext).modifiedAt
      : this.state.integrationInfo.createdAt;
      let date: Date = new Date(dateString);
      let validDate = date && !isNaN(date.getTime());
      connectedOn = validDate ? Formatters.formatMediumDateTimeWithoutSeconds(date) : Messages.connectToMsTeams_connectedOn_errorState();
    }

    return (
      <table id='msteams-connection-table-id' className='tb-static-grid-table tb-static-grid-table-settings-width'>
        <thead>
          <tr>
            {this.renderMsTeamsConnectionTableHeader()}
          </tr>
        </thead>
        <tbody id='msteams-connection-list-body-id'>
          <tr>
            <td><span id='msteams-connection-workspace-name' className='tb-data-grid-cell-text tb-lr-padded-wide'>{workspaceName}</span></td>
            <td><span id='msteams-connection-connected-on' className='tb-data-grid-cell-text tb-lr-padded-wide'>{connectedOn}</span></td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderMsTeamsConnectionTableHeader() {
    const tableHeaders = { [Messages.connectToMsTeams_msTeamsWorkspace()]: '', [Messages.connectToMsTeams_connectedOn()]: '' };
    let header = Object.keys(tableHeaders);
    return header.map((key, index) => {
      return (<th key={index} className='tb-data-grid-headers-line'>
        <span className='tb-data-grid-header-text'>{key}</span>
      </th>);
    });
  }

  /**
   * When the user presses "Disconnect from Microsoft Teams", the corresponding credential should be deleted
   * @param luid string that corresponds to the msTeams credential to be deleted
   * @returns Result of the delete call
   */
  disconnectFromMsTeams(luid: string): Promise<IResult> {
    const msTeamsCredentialsRequest = new DeleteCredentialRequest({ luid: luid, targetClass: OAuthCredentialTypes.MsTeams });
    return WebHttpClient.requestPost(msTeamsCredentialsRequest);
  }

  /**
   * We get into this situation in Online if SIP is enabled but the user has a V1/legacy connection.
   * We need to query the credential to delete and then call the delete credential API using that luid.
   * @returns Result of delete credential call.
   */
  disconnectFromMsTeamsLegacy(): () => Promise<IResult> {
    let getIntegrationInfoResult = getMsTeamsIntegrationSummaryOnPrem();
    return () => getIntegrationInfoResult.then(async (result: IListCredentialModelsResult) => {
        let credentials = result.credentialModels;
        return await this.disconnectFromMsTeams(credentials[0].luid);
      });
  }

  disconnectFromMsTeamsRest(): () => Promise<IResult> {
    const integrationId = this.getIntegrationId();
    return () => {
      return removeIntegrationFromSite(integrationId);
    };
  }

  handleConnectToMsTeams(alreadyConnected: boolean, id?: string) {
    const sessionManagementService = getSessionManagementServiceInstance();
    if (alreadyConnected) {
      let description = Messages.connectToMsTeams_disconnectFromMsTeams_modalDescription();
      let actionName = Messages.connectToMsTeams_disconnectFromMsTeams_actionName();
      let header = Messages.connectToMsTeams_disconnectFromMsTeams_header();

      let disconnectAction;
      if (isFromRest(this.state.integrationInfo) && isVirtualRecord(this.state.integrationInfo)) {
        disconnectAction = this.disconnectFromMsTeamsLegacy();
      } else if (isFromRest(this.state.integrationInfo)) {
        disconnectAction = this.disconnectFromMsTeamsRest();
      } else {
        disconnectAction = this.disconnectFromMsTeams;
      }

      this.openDeleteConfirmationDialog(description, header, actionName, disconnectAction, id).then((result) => {
        if (result.errors) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.connectToMsTeams_disconnectFromMsTeams_error() });
        } else {
          this.setState({ connected: false });
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToMsTeams_disconnectFromMsTeams_success() });
          sendConnectToMsTeamsTelemetry({
            userId: sessionManagementService.sessionUserId(),
            siteLuid: sessionManagementService.sessionSiteLuid(),
            connectToMsTeams: false
          });
        }
      }).catch((reason) => {
        if (reason !== 'cancel') {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.connectToMsTeams_disconnectFromMsTeams_error() });
        }
      });
    } else {
      AddOAuthCredentialAction.attemptAuth(OAuthCredentialTypes.MsTeams).then((result) => {
        if (result) {
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToMsTeams_toastSuccessMsg() });
          sendConnectToMsTeamsTelemetry({
            userId: sessionManagementService.sessionUserId(),
            siteLuid: sessionManagementService.sessionSiteLuid(),
            connectToMsTeams: true
          });
        }
        this.getMsTeamsIntegrationInfo();
      });
    }
  }

  /**
   * A confirmation dialog used for destructive actions on this page:
   * 1. Disconnecting from msTeams (deleting msTeams credential)
   * 2. Deleting MS Teams OAuth client
   * @param description The body test for the dialog
   * @param header The dialog title
   * @param actionName Wording for the destructive action (e.g. "Delete")
   * @param deleteAction The handler function
   * @param id Parameter used for the destructive action (ID associated with deleted credential/OAuth client)
   * @returns Dialog result
   */
  openDeleteConfirmationDialog(description: string, header: string, actionName: string, deleteAction: (id: string) => PromiseLike<IResult>, id: string): Promise<IResult> {
    const confirmationDialog = openReactConfirmationDialog<string, IResult>({
      title: header,
      message: description,
      paramsForAction: id,
      actionName,
      isActionDelete: true,
      dialogWidth: TabDialogWidth.width375px,
      action: () => deleteAction(id)
    });
    return confirmationDialog.result;
  }

  private isTableauOnPrem() {
    const sessionManagementService = getSessionManagementServiceInstance();
    return !sessionManagementService.isOnlineEnvironment();
  }

  getIntegrationInfoLuid() {
    if (isFromRest(this.state.integrationInfo)) {
      return undefined;
    }

    return this.state.integrationInfo.luid;
  }

  getIntegrationId() {
    if (isFromRest(this.state.integrationInfo)) {
      return this.state.integrationInfo.id;
    }

    return '';
  }

  getWorkspaceName() {
    if (isFromRest(this.state.integrationInfo)) {
      return this.state.integrationInfo.external.msTeams.name;
    }

    return this.state.integrationInfo.target.targetEndpoint;
  }

  getUpgradeRequired() {
    if (isFromRest(this.state.integrationInfo)) {
      return this.state.integrationInfo.isUpgradeRequired;
    }

    return false;
  }

  private setTermsAccepted(isAccepted: boolean) {
    this.setState({termsAccepted: isAccepted});
  }
}

function getMsTeamsIntegrationSummaryOnPrem() {
  const msTeamsIntegrationInfoRequest = new ListCredentialModelsRequest({ targetClass: OAuthCredentialTypes.MsTeams });
  return WebHttpClient.requestPost(msTeamsIntegrationInfoRequest);
}

/**
 * When Online and MsTeamsIntegrationPlatform is enabled, we make a rest call instead to get the
 * integration info. Since the types returned from the REST and non-REST methods are organized
 * differently, we need ot differentiate between what type was returned when getting certain
 * information.
 * 
 * @param model the credential model from state
 * @returns whether the model is type IntegrationContext, which is returned when a REST call is made
 */
function isFromRest(model: ICredentialModel | IntegrationContext): model is IntegrationContext {
  return (model as IntegrationContext).tableau !== undefined;
}

function isVirtualRecord(model: IntegrationContext) {
  return model.id === LegacyVirtualRecordId;
}

const mapStateToProps: MapStateToProps<MsTeamsIntegrationSettingsStateProps> = (state) => ({
  siteLuid: SessionSiteSelectors.siteLuid(state),
});

export const MsTeamsIntegrationSettings = connect(mapStateToProps)(MsTeamsIntegrationSettingsComponent);